import { type FormEvent, useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, fechaHoraLocal, mxn } from '../formato';
import { usePuede } from '../permisos';
import type { CasoImportacion, CatalogosLote, Lote, Vehiculo } from '../paginas/tipos';

const hoy = () => fechaHoraLocal(new Date()).slice(0, 10);
const COLORES = { importable: 'exito', revisar: 'aviso', no_importable: 'peligro' } as const;

/**
 * Caso de importación del título en mano al emplacado (arquitectura 2.15, sección 7): retiro
 * antes de que cobre el almacenaje, circulación temporal en EE. UU., traslado, despacho con el
 * pedimento comparado contra la cotización del agente, cruce, recepción y emplacado (REPUVE,
 * verificación, placas y tenencia). Los clásicos agregan la justificación ante el SAT.
 */
export function CasoImportacionVehiculo({
  v,
  lote,
  alRegistrar,
}: {
  v: Vehiculo;
  lote: Lote | null;
  alRegistrar: () => void;
}) {
  const api = useApi();
  const permitido = usePuede();
  const caso = useConsulta<CasoImportacion>(`/sourcing/importacion/casos/${v.id}`);
  const { datos: catalogos } = useConsulta<CatalogosLote>('/sourcing/subastas/catalogos');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [formulario, setFormulario] = useState<
    'abrir' | 'tag' | 'pedimento' | 'justificacion' | null
  >(null);
  const c = caso.datos;
  // 2.26: claves de pedimento de la vía del caso (Anexo 22, Ap. 2).
  const { datos: catalogoClaves } = useConsulta<{
    claves: { clave: string; nombre: string }[];
    vias: { tipo: string; claves: string[] }[];
  }>('/sourcing/importacion/vigente/claves-pedimento');
  const tipoCaso =
    c?.tipoImportacion ?? (c?.region === 'frontera' ? 'definitiva_frontera' : 'definitiva_resto');
  const clavesCaso = (catalogoClaves?.vias.find((x) => x.tipo === tipoCaso)?.claves ?? []).map(
    (clave) => ({
      clave,
      nombre: catalogoClaves?.claves.find((x) => x.clave === clave)?.nombre ?? clave,
    }),
  );
  const sinCaso = caso.estadoError === 404;
  const enDosSemanas = fechaHoraLocal(new Date(Date.now() + 14 * 86_400_000)).slice(0, 10);
  const titulo = v.titulo as 'limpio' | 'salvage' | 'rebuilt' | 'otro';

  const hacer = async (fn: () => Promise<unknown>, mensaje: string) => {
    setError(null);
    setAviso(null);
    try {
      await fn();
      setAviso(mensaje);
      caso.recargar();
      setFormulario(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const abrir = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim();
    void hacer(
      () =>
        api('POST', '/sourcing/importacion/casos', {
          vehiculoId: v.id,
          vin: v.vin,
          ...(v.anio && { anio: v.anio }),
          titulo,
          region: t('region'),
          fechaCruce: t('fechaCruce'),
          ...(v.marca && { marca: v.marca }),
          ...(v.modelo && { modelo: v.modelo }),
          ...(v.version && { version: v.version }),
          ...(t('valorAduanaMxn') && { valorAduanaMxn: t('valorAduanaMxn') }),
          ...(t('valorNadaMxn') && { valorNadaMxn: t('valorNadaMxn') }),
          ...(t('tipoImportacion') && { tipoImportacion: t('tipoImportacion') }),
          ...(t('aduanaEntrada') && { aduanaEntrada: t('aduanaEntrada') }),
          ...(lote?.datos.estadoEeuu && { estadoLote: lote.datos.estadoEeuu }),
          ...(t('almacenajeDesde') && {
            almacenaje: {
              desde: t('almacenajeDesde'),
              diasLibres: Number(t('diasLibres') || 0),
              ...(t('costoPorDiaUsd') && { costoPorDiaUsd: t('costoPorDiaUsd') }),
            },
          }),
        }),
      'Caso de importación abierto',
    );
  };
  const paso = (clave: string) =>
    hacer(
      () => api('POST', `/sourcing/importacion/casos/${v.id}/pasos/${clave}`, { fecha: hoy() }),
      'Paso registrado',
    );
  const emplacar = (clave: string) =>
    hacer(
      () =>
        api('POST', `/sourcing/importacion/casos/${v.id}/emplacado`, { paso: clave, fecha: hoy() }),
      'Emplacado actualizado',
    );
  const tag = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim();
    void hacer(
      () =>
        api('POST', `/sourcing/importacion/casos/${v.id}/circulacion-temporal`, {
          fecha: hoy(),
          ...(t('temporaryTag') && { temporaryTag: t('temporaryTag') }),
          ...(t('vigenteHasta') && { vigenteHasta: t('vigenteHasta') }),
          ...(t('aseguradora') && { aseguradora: t('aseguradora') }),
          ...(t('poliza') && { poliza: t('poliza') }),
        }),
      'Circulación temporal registrada',
    );
  };
  const pedimento = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || '0';
    void hacer(async () => {
      await api('POST', `/sourcing/importacion/casos/${v.id}/pedimento`, {
        numero: String(f.get('numero') ?? ''),
        fecha: String(f.get('fecha') ?? ''),
        ...(String(f.get('clave') ?? '') && { clave: String(f.get('clave')) }),
        igiMxn: t('igiMxn'),
        ivaMxn: t('ivaMxn'),
        dtaMxn: t('dtaMxn'),
        prevalidacionMxn: t('prevalidacionMxn'),
        honorariosMxn: t('honorariosMxn'),
        otrosMxn: t('otrosMxn'),
        ...(String(f.get('valorAduanaMxn') ?? '').trim() && {
          valorAduanaMxn: String(f.get('valorAduanaMxn')).trim(),
        }),
      });
      setTimeout(alRegistrar, 2500);
    }, 'Pedimento registrado: Finanzas reemplaza la estimación e Inventario marca «importado con pedimento»');
  };
  const justificar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void hacer(
      () =>
        api('POST', `/sourcing/importacion/casos/${v.id}/justificacion`, {
          fecha: hoy(),
          resultado: f.get('resultado'),
          ...(String(f.get('nota') ?? '').trim() && { nota: String(f.get('nota')).trim() }),
        }),
      'Justificación registrada',
    );
  };
  const puedePasos = permitido('importacion.pasos');
  const puedePedimento = permitido('importacion.pedimento');

  return (
    <section className="seccion">
      <header>
        <h2>Caso de importación</h2>
        <div style={{ flex: 1 }} />
        {sinCaso && permitido('importacion.abrir') && (
          <button
            className="boton primario"
            onClick={() => setFormulario(formulario === 'abrir' ? null : 'abrir')}
          >
            {formulario === 'abrir' ? 'Cancelar' : 'Abrir caso'}
          </button>
        )}
        {c?.estado === 'abierto' && puedePedimento && (
          <button
            className="boton primario"
            onClick={() => setFormulario(formulario === 'pedimento' ? null : 'pedimento')}
          >
            {formulario === 'pedimento' ? 'Cancelar' : 'Registrar pedimento'}
          </button>
        )}
      </header>
      {error && (
        <p className="error" style={{ margin: 16 }}>
          {error}
        </p>
      )}
      {aviso && (
        <p className="etiqueta exito" style={{ margin: 16 }}>
          {aviso}
        </p>
      )}
      {sinCaso && formulario !== 'abrir' && (
        <p className="vacio">Sin caso de importación. Ábrelo cuando el auto vaya a cruzar.</p>
      )}
      {formulario === 'abrir' && (
        <form className="cuerpo formulario" onSubmit={abrir}>
          <label className="campo">
            <span>Región de destino</span>
            <select name="region" defaultValue={lote?.region ?? 'resto'}>
              <option value="frontera">Región fronteriza</option>
              <option value="resto">Resto del país</option>
            </select>
          </label>
          <label className="campo">
            <span>Fecha estimada de cruce</span>
            <input name="fechaCruce" type="date" required defaultValue={enDosSemanas} />
          </label>
          <label className="campo">
            <span>Vía (vacío: la de menor costo)</span>
            <select name="tipoImportacion" defaultValue="">
              <option value="">Automática</option>
              {(catalogos?.tiposImportacion ?? []).map((t) => (
                <option key={t.clave} value={t.clave}>
                  {t.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Aduana de entrada</span>
            <select name="aduanaEntrada" defaultValue={lote?.aduanaSugerida ?? ''}>
              <option value="">Sugerida por el estado del lote</option>
              {(catalogos?.aduanas ?? []).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Valor en aduana a mano (MXN, si no hay cotización)</span>
            <input name="valorAduanaMxn" inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Valor NADA (clásicos, MXN)</span>
            <input name="valorNadaMxn" inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Días libres de almacenaje desde</span>
            <input name="almacenajeDesde" type="date" />
          </label>
          <label className="campo">
            <span>Días libres</span>
            <input name="diasLibres" type="number" min={0} max={60} defaultValue={3} />
          </label>
          <label className="campo">
            <span>Costo por día después (USD)</span>
            <input name="costoPorDiaUsd" inputMode="decimal" />
          </label>
          <div className="acciones">
            <button className="boton primario">Abrir caso</button>
          </div>
        </form>
      )}
      {c && (
        <div className="cuerpo" style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className={`etiqueta ${COLORES[c.semaforo]}`}>
              {c.semaforo === 'no_importable'
                ? 'No importable'
                : c.semaforo === 'revisar'
                  ? 'Revisar'
                  : 'Importable'}
            </span>
            <span className="etiqueta">{c.estado}</span>
            <span className="subtitulo">
              {c.tipoImportacion ?? 'sin vía'} ·{' '}
              {c.region === 'frontera' ? 'región fronteriza' : 'resto del país'} · aduana{' '}
              {c.aduanaEntrada ?? '—'} · cruce {fechaCorta(c.fechaCruceEstimada)}
            </span>
          </div>
          {c.avisos.map((a) => (
            <p key={a} className="etiqueta aviso">
              {a}
            </p>
          ))}
          {c.motivos.length > 0 && (
            <ul style={{ margin: 0 }}>
              {c.motivos.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
          {c.estimacion && (
            <p className="subtitulo">
              Importación estimada {mxn(c.estimacion['totalMxn'] ?? '0')} sobre un valor en aduana
              de {mxn(c.estimacion['valorAduanaMxn'] ?? '0')}
              {c.cotizacionId ? ' (cotización del agente)' : ' (aproximado)'}
            </p>
          )}
          {c.almacenaje && (
            <p className="subtitulo">
              Almacenaje gratis hasta el {fechaCorta(c.almacenaje.hasta)}
              {c.almacenaje.costoPorDiaUsd
                ? `; después ${c.almacenaje.costoPorDiaUsd} USD por día`
                : ''}
            </p>
          )}
          <table>
            <thead>
              <tr>
                <th>Paso</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(catalogos?.pasosCaso ?? []).map((p) => {
                const hecho = c.pasos[p.clave];
                const especial =
                  p.clave === 'circulacion_temporal' ||
                  p.clave === 'despacho' ||
                  p.clave === 'emplacado';
                return (
                  <tr key={p.clave}>
                    <td>{p.nombre}</td>
                    <td>
                      {hecho?.hecho ? (
                        <span className="etiqueta exito">
                          {hecho.fecha ? fechaCorta(hecho.fecha) : 'hecho'}
                        </span>
                      ) : (
                        <span className="etiqueta">pendiente</span>
                      )}
                    </td>
                    <td>
                      {!hecho?.hecho && puedePasos && !especial && c.estado !== 'bloqueado' && (
                        <button className="boton" onClick={() => void paso(p.clave)}>
                          Marcar hoy
                        </button>
                      )}
                      {!hecho?.hecho && puedePasos && p.clave === 'circulacion_temporal' && (
                        <button
                          className="boton"
                          onClick={() => setFormulario(formulario === 'tag' ? null : 'tag')}
                        >
                          Registrar tag y seguro
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {formulario === 'tag' && (
            <form className="formulario" onSubmit={tag}>
              <label className="campo">
                <span>Temporary tag</span>
                <input name="temporaryTag" maxLength={40} />
              </label>
              <label className="campo">
                <span>Vigente hasta</span>
                <input name="vigenteHasta" type="date" />
              </label>
              <label className="campo">
                <span>Aseguradora</span>
                <input name="aseguradora" maxLength={120} />
              </label>
              <label className="campo">
                <span>Póliza</span>
                <input name="poliza" maxLength={60} />
              </label>
              <div className="acciones">
                <button className="boton primario">Guardar</button>
              </div>
            </form>
          )}
          {c.circulacionTemporal && (
            <p className="subtitulo">
              Circulación temporal: tag {c.circulacionTemporal.temporaryTag ?? '—'}
              {c.circulacionTemporal.vigenteHasta
                ? ` hasta ${fechaCorta(c.circulacionTemporal.vigenteHasta)}`
                : ''}
              {c.circulacionTemporal.aseguradora ? ` · ${c.circulacionTemporal.aseguradora}` : ''}
            </p>
          )}
          {formulario === 'pedimento' && (
            <form className="formulario" onSubmit={pedimento}>
              <label className="campo">
                <span>Clave de pedimento</span>
                <select name="clave" required={clavesCaso.length > 0}>
                  {clavesCaso.map((x) => (
                    <option key={x.clave} value={x.clave}>
                      {x.clave} · {x.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <label className="campo">
                <span>Número de pedimento</span>
                <input name="numero" required placeholder="26 47 3456 6001234" />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input name="fecha" type="date" required defaultValue={hoy()} />
              </label>
              <label className="campo">
                <span>Valor en aduana real (MXN)</span>
                <input name="valorAduanaMxn" inputMode="decimal" />
              </label>
              <label className="campo">
                <span>IGI (MXN)</span>
                <input name="igiMxn" inputMode="decimal" defaultValue="0" />
              </label>
              <label className="campo">
                <span>IVA (MXN)</span>
                <input name="ivaMxn" inputMode="decimal" defaultValue="0" />
              </label>
              <label className="campo">
                <span>DTA (MXN)</span>
                <input name="dtaMxn" inputMode="decimal" defaultValue="0" />
              </label>
              <label className="campo">
                <span>Prevalidación (MXN)</span>
                <input name="prevalidacionMxn" inputMode="decimal" defaultValue="0" />
              </label>
              <label className="campo">
                <span>Honorarios del agente (MXN)</span>
                <input name="honorariosMxn" inputMode="decimal" defaultValue="0" />
              </label>
              <label className="campo">
                <span>Otros (MXN)</span>
                <input name="otrosMxn" inputMode="decimal" defaultValue="0" />
              </label>
              <div className="acciones">
                <button className="boton primario">Registrar pedimento</button>
              </div>
            </form>
          )}
          {c.pedimento && (
            <p className="subtitulo">
              Pedimento {c.pedimento['numero']} del {fechaCorta(c.pedimento['fecha'] ?? '')}
              {c.pedimento.comparacion &&
                ` · valor en aduana real ${mxn(c.pedimento.comparacion.realMxn)} contra ${mxn(c.pedimento.comparacion.cotizadoMxn)} cotizado (diferencia ${mxn(c.pedimento.comparacion.diferenciaMxn)})`}
            </p>
          )}
          {c.estado === 'despachado' && (
            <div>
              <h3 style={{ margin: '0 0 8px' }}>
                Emplacado {c.emplacadoCompleto ? '· completo' : ''}
              </h3>
              <div className="acciones" style={{ flexWrap: 'wrap' }}>
                {(catalogos?.pasosEmplacado ?? []).map((p) =>
                  c.emplacado[p.clave] ? (
                    <span key={p.clave} className="etiqueta exito">
                      {p.nombre} · {fechaCorta(c.emplacado[p.clave]?.fecha ?? '')}
                    </span>
                  ) : puedePasos ? (
                    <button key={p.clave} className="boton" onClick={() => void emplacar(p.clave)}>
                      {p.nombre}: hecho hoy
                    </button>
                  ) : (
                    <span key={p.clave} className="etiqueta">
                      {p.nombre}: pendiente
                    </span>
                  ),
                )}
              </div>
            </div>
          )}
          {c.tipoImportacion === 'clasico' && (
            <div>
              <h3 style={{ margin: '0 0 8px' }}>Justificación ante el SAT (clásico)</h3>
              {c.justificaciones.map((j, i) => (
                <p key={i} className="subtitulo">
                  {fechaCorta(j.fecha)} · {j.resultado}
                  {j.nota ? ` · ${j.nota}` : ''}
                </p>
              ))}
              {puedePedimento && (
                <>
                  <button
                    className="boton"
                    onClick={() =>
                      setFormulario(formulario === 'justificacion' ? null : 'justificacion')
                    }
                  >
                    Registrar intento
                  </button>
                  {formulario === 'justificacion' && (
                    <form className="formulario" onSubmit={justificar} style={{ marginTop: 12 }}>
                      <label className="campo">
                        <span>Resultado</span>
                        <select name="resultado" defaultValue="pendiente">
                          <option value="pendiente">En trámite</option>
                          <option value="justificado">Justificado</option>
                          <option value="no_justificado">No justificado</option>
                        </select>
                      </label>
                      <label className="campo">
                        <span>Nota</span>
                        <input name="nota" maxLength={500} />
                      </label>
                      <div className="acciones">
                        <button className="boton primario">Guardar</button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
